import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Seo, breadcrumbLd } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CommunityCard } from "@/components/CommunityCard";
import { MapView } from "@/components/MapView";
import { get, feeOrNA } from "@/lib/api";
import NotFound from "@/pages/NotFound";

export default function CityPage() {
  const { state, city } = useParams();
  const { data, isError, isLoading } = useQuery({ queryKey: ["city", state, city], queryFn: () => get(`/states/${state}/${city}`) });
  if (isError) return <NotFound />;
  if (isLoading) return <div className="max-w-7xl mx-auto p-12 text-slate-600">Loading…</div>;
  const crumbs = [{ name: "Home", to: "/" }, { name: data.state, to: `/${state}` }, { name: data.city, to: `/${state}/${city}` }];
  const points = data.items.filter((i) => i.latitude != null);

  return (
    <div data-testid="city-page">
      <Seo title={`${data.city}, ${data.state_code} HOAs - Fees & Rules`} path={`/${state}/${city}`}
        description={`${data.items.length} HOA communities in ${data.city}, ${data.state}. Compare monthly HOA fees, pet policies and rental restrictions.`}
        jsonLd={[breadcrumbLd(crumbs), {
          "@context": "https://schema.org", "@type": "ItemList", name: `HOAs in ${data.city}, ${data.state_code}`,
          itemListElement: data.items.slice(0, 50).map((c, i) => ({ "@type": "ListItem", position: i + 1, url: `${window.location.origin}/${c.state_slug}/${c.city_slug}/${c.slug}`, name: c.name })),
        }]} />
      <PageHeader eyebrow={`${data.items.length} communities`} title={`${data.city}, ${data.state_code} HOAs`}
        subtitle={`Average monthly fee ${feeOrNA(data.avg_fee)} · ${data.pet_friendly} pet-friendly communities.`}>
        <Breadcrumbs items={crumbs} />
      </PageHeader>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 grid gap-6 sm:gap-8 lg:grid-cols-[1fr_380px]">
        <div className="grid gap-5 sm:grid-cols-2 content-start">{data.items.map((c) => <CommunityCard key={c.id} c={c} />)}</div>
        {points.length > 0 && <div className="h-[420px] lg:sticky lg:top-28"><MapView points={points} testid="city-map" /></div>}
      </div>
    </div>
  );
}
